import React from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { Check, Key, MapPin, ChevronDown, ArrowUpRight, Plus, Minus } from 'lucide-react';
import { TRADER_FALLBACK_IMAGES } from '../../utils/constants';

const QUEST_NAME_TRANSLATIONS = {
  "Debut": "Sahneye Çıkış",
  "Search Mission": "Arama Görevi",
  "The Survivalist Path - Eagle-Owl": "Hayatta Kalma Yolu - Puhu Kuşu"
};

const getWikiLink = (quest, language) => {
  if (language === 'ru') {
    const formatted = quest.name.trim().replace(/ /g, '_');
    return `https://escapefromtarkov.fandom.com/ru/wiki/${formatted}`;
  }
  // For en/tr use the API-provided English wiki link
  if (quest.wikiLink) return quest.wikiLink;
  const formatted = (quest.englishName || quest.name).trim().replace(/ /g, '_');
  return `https://escapefromtarkov.fandom.com/wiki/${formatted}`;
};

function QuestCard({
  quest,
  isCompleted,
  isExpanded,
  setExpandedQuest,
  completedObjectives,
  questProgress,
  handleQuestComplete,
  handleObjectiveToggle,
  handleProgressChange,
  tColor,
  language,
  t
}) {
  const questObjs = completedObjectives[quest.id] || [];
  const requiredObjectives = quest.objectives.filter(obj => !obj.optional);
  const completedRequired = requiredObjectives.filter(obj => questObjs.includes(obj.id)).length;
  const neededKeys = quest.neededKeys || [];
  const hasKeyReq = neededKeys.length > 0;

  const traderImage = quest.trader?.imageLink || TRADER_FALLBACK_IMAGES[quest.trader?.normalizedName] || TRADER_FALLBACK_IMAGES['default'];

  return (
    <div
      className={`group border rounded-sm transition-all duration-200 overflow-hidden relative ${isCompleted ? 'opacity-60 hover:opacity-100' : 'shadow-md'}`}
      style={{
        backgroundColor: isExpanded ? '#0c0c0e' : '#09090b',
        borderColor: isExpanded ? `${tColor}40` : 'rgba(255,255,255,0.05)'
      }}
    >
      <div className={`h-full w-1 absolute left-0 top-0 bottom-0 transition-colors`} style={{ backgroundColor: isCompleted ? '#10b981' : tColor }}></div>

      {/* QUEST HEADER */}
      <div className="flex items-center gap-4 p-4 pl-5 cursor-pointer" onClick={() => setExpandedQuest(isExpanded ? null : quest.id)}>
        <div className="relative">
          {traderImage && (
            <LazyLoadImage src={traderImage} alt="Trader" effect="blur" className="w-12 h-12 rounded-sm object-cover ring-1 ring-zinc-800 grayscale group-hover:grayscale-0 transition-all" />
          )}
          <div className="absolute -bottom-1 -right-1 bg-black rounded-sm border border-zinc-800 px-1">
            <span className="text-[9px] font-mono font-bold text-zinc-400 block leading-none">{quest.minPlayerLevel}</span>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`text-sm font-bold truncate uppercase tracking-wide transition-colors ${isCompleted ? 'text-emerald-500 line-through decoration-2' : 'text-zinc-200'}`}
              style={!isCompleted ? { color: isExpanded ? tColor : undefined } : {}}
            >
              {language === 'tr' ? (QUEST_NAME_TRANSLATIONS[quest.englishName] || quest.name) : quest.name}
            </span>

            <div className="flex items-center gap-1.5">
              {quest.kappaRequired && (
                <span className="bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 text-[9px] px-1.5 py-px rounded-sm font-mono font-bold uppercase tracking-wider">KAPPA</span>
              )}
              {hasKeyReq && (
                <span className="bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 text-[9px] px-1.5 py-px rounded-sm font-mono font-bold uppercase tracking-wider flex items-center gap-1">
                  <Key className="w-2.5 h-2.5" /> KEY
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-zinc-500 font-mono">
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              <span className="uppercase">{quest.mapName}</span>
            </div>
            <div className="w-px h-3 bg-zinc-800"></div>
            {!isCompleted ? (
              <span className={`${completedRequired === requiredObjectives.length ? 'text-emerald-500' : 'text-zinc-500'}`}>
                {completedRequired} / {requiredObjectives.length} {t('objectives') || 'Objectives'}
              </span>
            ) : (
              <span className="text-emerald-600 font-bold flex items-center gap-1">
                <Check className="w-3 h-3" /> {t('completed') || 'Completed'}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={(e) => { e.stopPropagation(); handleQuestComplete(quest.id); }}
            className={`w-8 h-8 flex items-center justify-center rounded-sm border transition-all ${isCompleted ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-zinc-900 border-zinc-700 text-zinc-500 hover:border-emerald-500 hover:text-emerald-500'}`}
            title="Complete Quest"
          >
            <Check className="w-4 h-4" strokeWidth={3} />
          </button>
          <div className="w-px h-6 bg-zinc-800"></div>
          <a
            href={getWikiLink(quest, language)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1 px-2 py-1 bg-zinc-900 text-zinc-400 hover:text-blue-400 rounded-sm hover:bg-blue-500/10 transition text-[10px] font-bold uppercase tracking-wider border border-transparent hover:border-blue-500/20"
          >
            <span>Wiki</span>
            <ArrowUpRight className="w-3 h-3" />
          </a>
          <div className={`text-zinc-600 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} style={{ color: isExpanded ? tColor : undefined }}>
            <ChevronDown className="w-4 h-4" />
          </div>
        </div>
      </div>

      {/* EXPANDED CONTENT (OBJECTIVES) */}
      {isExpanded && (
        <div className="border-t border-zinc-800 bg-[#0c0c0e] p-4 pl-14">
          <div className="space-y-2">
            {quest.objectives.length > 0 ? (
              quest.objectives.map(obj => {
                const isObjCompleted = questObjs.includes(obj.id);

                const targetCount = (obj.count && obj.count > 1) ? obj.count : 1;

                const hasCounter = targetCount > 1;
                const currentCount = questProgress[obj.id] || 0;

                return (
                  <div key={obj.id} className={`flex flex-col sm:flex-row sm:items-center gap-3 p-2.5 rounded-sm border transition-all ${isObjCompleted ? 'bg-emerald-900/10 border-emerald-500/20' : 'bg-[#09090b] border-zinc-800'}`}>
                    <div className="flex-1 flex items-start gap-3">
                      <button
                        onClick={() => handleObjectiveToggle(quest.id, obj.id)}
                        className={`mt-0.5 w-4 h-4 rounded-sm flex-shrink-0 flex items-center justify-center transition-all border ${isObjCompleted ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-zinc-900 border-zinc-700 text-transparent hover:border-zinc-500'}`}
                      >
                        <Check className="w-3 h-3" strokeWidth={4} />
                      </button>
                      <div>
                        <p className={`text-xs font-medium leading-relaxed ${isObjCompleted ? 'text-zinc-500 line-through' : 'text-zinc-300'}`}>
                          {obj.description}
                        </p>
                      </div>
                    </div>

                    {hasCounter && (
                      <div className="flex items-center bg-black border border-zinc-700 rounded-sm overflow-hidden h-7">
                        <button
                          onClick={() => handleProgressChange(quest.id, obj.id, targetCount, -1)}
                          className="px-2 h-full bg-zinc-900 hover:bg-zinc-800 text-zinc-400 border-r border-zinc-700 transition-colors flex items-center justify-center"
                        >
                          <Minus className="w-3 h-3" />
                        </button>

                        <div className="px-3 h-full flex items-center gap-1 min-w-[50px] justify-center bg-[#050505]">
                          <span className={`text-xs font-mono font-bold ${isObjCompleted ? 'text-emerald-500' : 'text-white'}`}>
                            {isObjCompleted ? targetCount : currentCount}
                          </span>
                          <span className="text-[9px] text-zinc-600 font-mono">/ {targetCount}</span>
                        </div>

                        <button
                          onClick={() => handleProgressChange(quest.id, obj.id, targetCount, 1)}
                          className="px-2 h-full bg-zinc-900 hover:bg-zinc-800 text-zinc-400 border-l border-zinc-700 transition-colors flex items-center justify-center"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center text-zinc-600 py-2 text-xs font-mono">No specific objectives listed. Check Wiki.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default QuestCard;
